import { Suspense, lazy, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useParams,
} from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AuthGuard from "@/components/auth/AuthGuard";
import AdminShell from "@/components/layout/AdminShell";
import { FullPageSpinner } from "@/components/layout/PageSkeleton";
import { ADMIN_REDIRECTS, ADMIN_ROUTES } from "@/navigation/registry";
import LoginPage from "@/pages/LoginPage";
import NoStaffAccess from "@/components/auth/NoStaffAccess";
import NotFound from "@/pages/NotFound";

/*
 * Every signed-in page lives in src/navigation/registry.ts with its required
 * roles. This file only turns that registry into routes:
 *   public auth routes → persistent AdminShell (RequireStaff) → per-route AuthGuard.
 * A data router is used so the book editor can block navigation with unsaved changes.
 */

const InitialSetupPage = lazy(() => import("@/pages/InitialSetupPage"));
const InvitationRedirect = lazy(() => import("@/pages/InvitationRedirect"));

/** /admin/setup only exists until a Super Admin does. */
const SetupGate = () => {
  const [setupCompleted, setSetupCompleted] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.from("user_to_roles").select("id").eq("role_id", 1).limit(1);
        if (error) throw error;
        if (!cancelled) setSetupCompleted(!!data?.length);
      } catch {
        if (!cancelled) setSetupCompleted(localStorage.getItem("initial_setup_complete") === "true");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (setupCompleted === null) return <FullPageSpinner label="Checking set-up" />;
  if (setupCompleted) return <Navigate to="/admin/login" replace />;
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <InitialSetupPage />
    </Suspense>
  );
};

/** Keeps :params when an old URL redirects to its canonical page. */
const ParamRedirect = ({ to }: { to: string }) => {
  const params = useParams();
  const target = to.replace(/:(\w+)/g, (_, k: string) => params[k] ?? "");
  return <Navigate to={target} replace />;
};

const lazyPublic = (el: JSX.Element) => <Suspense fallback={<FullPageSpinner />}>{el}</Suspense>;

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/" element={<Navigate to="/admin/today" replace />} />

      {/* ===== Public auth routes ===== */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/setup" element={<SetupGate />} />
      <Route path="/admin/standard-user" element={<NoStaffAccess />} />
      {/* Passwords are gone: old reset links land on sign-in. */}
      <Route path="/admin/reset-password" element={<Navigate to="/admin/login" replace />} />
      <Route path="/reset-password" element={<Navigate to="/admin/login" replace />} />
      <Route path="/auth/invite" element={lazyPublic(<InvitationRedirect />)} />
      <Route path="/auth/v1/verify" element={lazyPublic(<InvitationRedirect />)} />
      <Route path="/verify" element={lazyPublic(<InvitationRedirect />)} />

      {/* ===== Signed-in admin: one persistent shell ===== */}
      <Route element={<AdminShell />}>
        {ADMIN_ROUTES.map(({ path, roles, page: Page }) => (
          <Route
            key={path}
            path={path}
            element={
              <AuthGuard requiredRoles={roles}>
                <Page />
              </AuthGuard>
            }
          />
        ))}
        {ADMIN_REDIRECTS.map(({ from, to }) => (
          <Route key={from} path={from} element={<ParamRedirect to={to} />} />
        ))}
        <Route path="/admin/*" element={<NotFound />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </>
  )
);
